import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { IconArmchair } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { getGuestSeatInfo } from "@/lib/api/clients/SeatingClient";
import { seatStatusChipClasses } from "@/lib/seating/seatStatusChip";
import { seatLabelText, seatPlaceText } from "@/lib/seating/seatNumber";
import type { SeatEntry } from "@/types/Seating";
import { PanelEmptyState } from "./PanelEmptyState";

type SeatingInfoProps = {
  guestId: string;
};

export const SeatingInfo: React.FC<SeatingInfoProps> = ({ guestId }) => {
  const t = useTranslations("guestDetails");
  const tSeating = useTranslations("seating");
  const params = useParams();
  const eventId = params.eventId as string;
  const [loading, setLoading] = useState(true);
  const [seats, setSeats] = useState<SeatEntry[]>([]);
  const [error, setError] = useState(false);

  // A guest can hold seats in several rooms. `seats` carries them all; the
  // scalar fields on the response still describe the primary seat, so an older
  // backend that sends only those still renders — hence the fallback.
  useEffect(() => {
    if (!guestId || !eventId) return;
    // Was guarded by an `executed` ref, which made these deps inert: switching
    // the rail to another guest (or re-seating in a second tab) left the first
    // guest's table on screen forever.
    let cancelled = false;
    setLoading(true);
    setError(false);
    getGuestSeatInfo(eventId, guestId)
      .then((result) => {
        if (cancelled) return;
        const list = Array.isArray(result?.seats)
          ? result.seats
          : result && !Array.isArray(result)
            ? [result as SeatEntry]
            : [];
        setSeats(list);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [guestId, eventId]);

  return (
    <div className="flex flex-col h-full overflow-y-auto justify-between">
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="p-4 text-center font-error body-2-regular">
          {tSeating("guests.loadError")}
        </div>
      ) : seats.length === 0 ? (
        <PanelEmptyState riv="NoRoom" title={t("notSeated")} />
      ) : (
        <div className="mt-3 space-y-3">
          {seats.map((s) => (
            // The whole card is the "find this seat" door: it opens the plan on
            // the right room and hands it `seat=` so the canvas centres on the
            // seat and pulses it. `roomId` is optional on older backends — the
            // plan resolves the room from the furniture either way, the segment
            // only spares us a flash of the room overview first.
            <Link
              key={`${s.furnitureId}-${s.seatIndex}`}
              to={`/event/${eventId}/seating${s.roomId ? `/${s.roomId}` : ""}?furnitureId=${s.furnitureId}&seat=${s.seatIndex}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-xl border outline-quaternary surface-primary p-4 transition-colors hover:surface-secondary hover:outline-tertiary focus-ring"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full body-4-bold ${seatStatusChipClasses(s.status)}`}
                >
                  {tSeating(`seats.${s.status}` as "seats.assigned")}
                </span>
                <IconArmchair
                  size={16}
                  // Not `opacity-0`: on touch there is no hover, and the card
                  // would then carry no hint at all that it goes anywhere.
                  className="font-tertiary opacity-40 transition-opacity group-hover:opacity-100"
                />
              </div>
              {s.roomName ? (
                <p className="body-3-regular font-tertiary mb-0.5">{s.roomName}</p>
              ) : null}
              {/* The piece's block and area (seats.io Teil 2, Zug 3) in front of
                  its name — "Parkett · Block C · Stuhlblock 1"; without a block
                  exactly the name, as always. */}
              <p className="body-2-bold font-primary">{seatPlaceText(s, s.furnitureName)}</p>
              <p className="body-3-regular font-tertiary mt-1">
                {/* The printed seat (server-stamped `seatNumber`/`seatRow`, else
                    `seatIndex` plus one); the link above keeps the 0-based index. */}
                {seatLabelText(tSeating, s)}
              </p>
            </Link>
          ))}
        </div>
      )}
      <div className="w-full flex justify-end items-end mb-2">
        <Link
          to={`/event/${eventId}/seating`}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full p-3 border outline-tertiary"
        >
          <IconArmchair size={20} className="font-secondary" />
        </Link>
      </div>
    </div>
  );
};
